import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
  setDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

// Point values for different tasks
export const POINT_VALUES = {
  TWITTER_SHARE: 50,
  TELEGRAM_SHARE: 50,
  REFERRAL_SIGNUP: 100, // Points for referrer when someone signs up
  REFERRAL_BONUS: 50, // Points for new user who uses referral
  FUTURE_TRADE: 10, // Base points per trade
  FUTURE_TRADE_MULTIPLIER: 0.1, // Additional points per $1 traded
  PREDICTION_TRADE: 10, // Base points per prediction
  PREDICTION_TRADE_MULTIPLIER: 0.1, // Additional points per $1 predicted
};

/**
 * Calculate points for a trade based on amount
 * @param {number} amount - Trade amount in USDT
 * @param {string} type - 'future' or 'prediction'
 * @returns {number} Points earned
 */
export const calculateTradePoints = (amount, type = 'future') => {
  const basePoints = type === 'future' 
    ? POINT_VALUES.FUTURE_TRADE 
    : POINT_VALUES.PREDICTION_TRADE;
  const multiplier = type === 'future'
    ? POINT_VALUES.FUTURE_TRADE_MULTIPLIER
    : POINT_VALUES.PREDICTION_TRADE_MULTIPLIER;
  
  return Math.floor(basePoints + (amount * multiplier));
};

/**
 * Add points to user account
 * @param {string} address - Wallet address
 * @param {number} points - Points to add
 * @param {string} reason - Reason for points (e.g., 'twitter_share', 'referral', 'trade')
 * @param {object} metadata - Additional metadata
 * @returns {Promise<void>}
 */
export const addPoints = async (address, points, reason, metadata = {}) => {
  try {
    if (!address || !points || points <= 0) {
      throw new Error("Invalid address or points");
    }

    const normalizedAddress = address.toLowerCase();
    const userRef = doc(db, "users", normalizedAddress);
    const userDoc = await getDoc(userRef);

    // Update user's total points
    if (userDoc.exists()) {
      await updateDoc(userRef, {
        totalPoints: increment(points),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Create user document if it doesn't exist
      await setDoc(userRef, {
        address: normalizedAddress,
        totalPoints: points,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    // Record point transaction
    const transactionData = {
      userAddress: normalizedAddress,
      points: points,
      reason: reason,
      metadata: metadata,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    };

    await addDoc(collection(db, "pointTransactions"), transactionData);
  } catch (error) {
    console.error("Error adding points:", error);
    throw error;
  }
};

/**
 * Get user's total points
 * @param {string} address - Wallet address
 * @returns {Promise<number>} Total points
 */
export const getUserPoints = async (address) => {
  try {
    if (!address) return 0;

    const normalizedAddress = address.toLowerCase();
    const userRef = doc(db, "users", normalizedAddress);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.totalPoints || 0;
    }

    return 0;
  } catch (error) {
    console.error("Error getting user points:", error);
    return 0;
  }
};

/**
 * Get user's point transaction history
 * @param {string} address - Wallet address
 * @param {number} limit - Maximum number of transactions to return
 * @returns {Promise<Array>} Array of point transactions
 */
export const getPointTransactions = async (address, limit = 50) => {
  try {
    if (!address) return [];

    const normalizedAddress = address.toLowerCase();
    const q = query(
      collection(db, "pointTransactions"),
      where("userAddress", "==", normalizedAddress),
      // Note: You may need to create a Firestore index for this query
    );

    const querySnapshot = await getDocs(q);
    const transactions = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Sort by timestamp (newest first) and limit
    transactions.sort((a, b) => {
      const timeA = a.timestamp?.toDate?.()?.getTime() || 
                    a.timestamp?.getTime() || 
                    (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const timeB = b.timestamp?.toDate?.()?.getTime() || 
                    b.timestamp?.getTime() || 
                    (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return timeB - timeA;
    });

    return transactions.slice(0, limit);
  } catch (error) {
    console.error("Error getting point transactions:", error);
    return [];
  }
};

/**
 * Check if user has already claimed points for a specific action
 * @param {string} address - Wallet address
 * @param {string} reason - Reason to check (e.g., 'twitter_share', 'telegram_share')
 * @returns {Promise<boolean>} True if already claimed
 */
export const hasClaimedPoints = async (address, reason) => {
  try {
    if (!address || !reason) return false;

    const normalizedAddress = address.toLowerCase();
    const q = query(
      collection(db, "pointTransactions"),
      where("userAddress", "==", normalizedAddress),
      where("reason", "==", reason)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking claimed points:", error);
    return false;
  }
};

/**
 * Award points for social sharing
 * @param {string} address - Wallet address
 * @param {string} platform - 'twitter' or 'telegram'
 * @returns {Promise<object>} Result object with success status
 */
export const awardSocialSharePoints = async (address, platform) => {
  try {
    if (!address) {
      return { success: false, error: "Wallet address required" };
    }

    const reason = platform === 'twitter' ? 'twitter_share' : 'telegram_share';
    const points = platform === 'twitter' 
      ? POINT_VALUES.TWITTER_SHARE 
      : POINT_VALUES.TELEGRAM_SHARE;

    // Check if already claimed
    const alreadyClaimed = await hasClaimedPoints(address, reason);
    if (alreadyClaimed) {
      return { success: false, error: "Points already claimed for this action" };
    }

    await addPoints(address, points, reason, { platform });
    return { success: true, points };
  } catch (error) {
    console.error("Error awarding social share points:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Award points for a trade
 * @param {string} address - Wallet address
 * @param {number} amount - Trade amount
 * @param {string} type - 'future' or 'prediction'
 * @param {object} metadata - Additional trade metadata
 * @returns {Promise<object>} Result object
 */
export const awardTradePoints = async (address, amount, type = 'future', metadata = {}) => {
  try {
    if (!address || !amount || amount <= 0) {
      return { success: false, error: "Invalid address or amount" };
    }

    const points = calculateTradePoints(amount, type);
    const reason = type === 'future' ? 'future_trade' : 'prediction_trade';

    await addPoints(address, points, reason, {
      amount,
      type,
      ...metadata,
    });

    return { success: true, points };
  } catch (error) {
    console.error("Error awarding trade points:", error);
    return { success: false, error: error.message };
  }
};
